import _ from 'lodash';
import { defineStore } from 'pinia';
import { getClient } from '~/util/awclient';
import { useServerStore } from '~/stores/server';
function select_buckets(buckets, { host, type }) {
    return _.map(_.filter(buckets, bucket => (!type || bucket.type === type) && (!host || bucket.hostname == host)), bucket => bucket['id']);
}
export const useBucketsStore = defineStore('buckets', {
    state: () => ({
        buckets: [],
    }),
    getters: {
        hosts(state) {
            // TODO: Include consideration of device_id UUID
            let hosts = _.uniq(_.map(state.buckets, bucket => bucket.hostname || bucket.data.hostname));
            // Sort priority:
            //   1. Host that matches the server's own hostname (the device the
            //      webui is running on) — almost always what the user wants by
            //      default in views like Alerts/Trends.
            //   2. Hosts that actually have the buckets we typically query against
            //      (window + AFK), so views don't pick a stale legacy hostname
            //      with only one orphan bucket and then immediately error out.
            //   3. Then by last_updated, newest first (legacy behavior).
            const serverStore = useServerStore();
            const selfHost = serverStore.info && serverStore.info.hostname;
            hosts = _.orderBy(hosts, [
                host => (host && host === selfHost ? 1 : 0),
                host => {
                    const hasWindow = this.bucketsWindow(host).length > 0;
                    const hasAfk = this.bucketsAFK(host).length > 0;
                    return hasWindow && hasAfk ? 2 : hasWindow || hasAfk ? 1 : 0;
                },
                host => _.max(_.map(this.bucketsByHostname[host], b => b.last_updated)) || '',
            ], ['desc', 'desc', 'desc']);
            return hosts;
        },
        // Uses device_id instead of hostname
        devices() {
            // TODO: Include consideration of device_id UUID
            return _.uniq(_.map(this.buckets, bucket => bucket.device_id || bucket.data.device_id));
        },
        available() {
            // Returns a map of which kinds of buckets are available
            //
            // 'window' requires ((currentwindow + afkstatus) or android) buckets
            // 'browser' requires (currentwindow + afk + browser) buckets
            // 'editor' requires editor buckets
            return hostname => {
                const windowAvail = this.bucketsWindow(hostname).length > 0 && this.bucketsAFK(hostname).length > 0;
                const androidAvail = this.bucketsAndroid(hostname).length > 0;
                return {
                    window: windowAvail,
                    browser: windowAvail && this.bucketsBrowser(hostname).length > 0,
                    editor: this.bucketsEditor(hostname).length > 0,
                    android: androidAvail,
                    category: windowAvail || androidAvail,
                    stopwatch: this.bucketsStopwatch(hostname).length > 0,
                };
            };
        },
        bucketsByType() {
            return (host, type, fallback_unknown_host) => {
                let buckets = select_buckets(this.buckets, { host, type });
                if (fallback_unknown_host && buckets.length == 0) {
                    buckets = select_buckets(this.buckets, { host: 'unknown', type });
                    //console.log('fallback: ', buckets);
                }
                return buckets;
            };
        },
        // Convenience getters for bucketsByType
        bucketsAFK() {
            return host => this.bucketsByType(host, 'afkstatus');
        },
        bucketsWindow() {
            return host => this.bucketsByType(host, 'currentwindow').filter((id) => !id.startsWith('aw-watcher-android'));
        },
        bucketsAndroid() {
            return host => this.bucketsByType(host, 'currentwindow').filter((id) => id.startsWith('aw-watcher-android'));
        },
        bucketsEditor() {
            // fallback to a bucket with 'unknown' host, if one exists.
            // TODO: This needs a fix so we can get rid of this workaround.
            return host => this.bucketsByType(host, 'app.editor.activity', true);
        },
        bucketsBrowser() {
            // fallback to a bucket with 'unknown' host, if one exists.
            // TODO: This needs a fix so we can get rid of this workaround.
            return host => this.bucketsByType(host, 'web.tab.current', true);
        },
        bucketsStopwatch() {
            // fallback to a bucket with 'unknown' host, if one exists.
            // TODO: This needs a fix so we can get rid of this workaround.
            return (host) => this.bucketsByType(host, 'general.stopwatch', true);
        },
        getBucket() {
            return id => _.filter(this.buckets, b => b.id === id)[0];
        },
        bucketsByHostname() {
            return _.groupBy(this.buckets, 'hostname');
        },
        // Group all buckets by their device.
        // Returns a dict with buckets by device/host (hostname or device_id)
        //
        // First element will be the current hostname/device, if present.
        // Others sorted by last_updated.
        bucketsByDevice: function () {
            let devices = _.mapValues(_.groupBy(this.buckets, b => b.hostname || b.device_id), d => {
                const hostnames = _.uniq(_.map(d, b => b.hostname || b.data.hostname));
                const device_ids = _.uniq(_.map(d, b => b.data.device_id || b.hostname));
                return {
                    buckets: d,
                    device_id: device_ids[0],
                    device_ids,
                    hostname: hostnames[0],
                    hostnames,
                    first_seen: _.min(_.map(d, b => b.first_seen)),
                    last_updated: _.max(_.map(d, b => b.last_updated)),
                };
            });
            // Sort by last_updated
            const sortObjectByUpdated = _.flow([
                _.toPairs,
                pairs => _.orderBy(pairs, pair => pair[1].last_updated, ['desc']),
                _.fromPairs,
            ]);
            devices = sortObjectByUpdated(devices);
            // find self-device and put first
            const serverStore = useServerStore();
            const hostname = serverStore.info && serverStore.info.hostname;
            const currentDevice = Object.prototype.hasOwnProperty.call(devices, hostname)
                ? devices[hostname]
                : null;
            if (currentDevice) {
                // remove self from list
                delete devices[hostname];
                // add self-device back to the top;
                devices = { [hostname]: currentDevice, ...devices };
            }
            return devices;
        },
    },
    actions: {
        async ensureLoaded() {
            if (this.buckets.length === 0) {
                await this.loadBuckets();
            }
        },
        async loadBuckets() {
            const buckets = await getClient().getBuckets();
            this.update_buckets(buckets);
        },
        async getBucketWithEvents({ id, start, end, limit, }) {
            await this.ensureLoaded();
            const bucket = _.cloneDeep(this.getBucket(id));
            bucket.events = await getClient().getEvents(bucket.id, {
                start,
                end,
                limit: limit || -1,
            });
            return bucket;
        },
        async getBucketsWithEvents({ start, end }) {
            await this.ensureLoaded();
            const buckets = await Promise.all(_.map(this.buckets, async (bucket) => await this.getBucketWithEvents({ id: bucket.id, start, end })));
            return _.orderBy(buckets, [b => b.id], ['asc']);
        },
        async deleteBucket({ bucketId }) {
            console.log(`Deleting bucket ${bucketId}`);
            await getClient().deleteBucket(bucketId);
            await this.loadBuckets();
        },
        async deleteBucketsByHost({ bucketIds }) {
            console.log(`Deleting ${bucketIds.length} buckets`);
            try {
                for (const bucketId of bucketIds) {
                    await getClient().deleteBucket(bucketId);
                }
            }
            finally {
                await this.loadBuckets().catch(err => {
                    console.warn('Failed to reload buckets after deletion:', err);
                });
            }
            return bucketIds;
        },
        // mutations
        update_buckets(buckets) {
            this.buckets = _.orderBy(buckets, [b => b.id], ['asc']).map(b => {
                // Some harmonization as aw-server-rust and aw-server-python APIs diverge slightly
                if (!b.last_updated && b.metadata && b.metadata.end) {
                    b.last_updated = b.metadata.end;
                }
                if (!b.first_seen && b.metadata && b.metadata.start) {
                    b.first_seen = b.metadata.start;
                }
                if (!b.first_seen && b.created) {
                    b.first_seen = b.created;
                }
                return b;
            });
        },
    },
});
